import { useAIAssistantForPage } from '@euler/app/components/ai/pageContext';
import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { PlanDetailEditPatch } from '@euler/app/flows/feed/components/message-body/common/PlanDetailActionsButton';
import { FooterView } from '@euler/app/flows/home/components/FooterView';
import { PlanDetailGroupCard } from '@euler/app/flows/plans/pages/list/components/PlanDetailGroupCard';
import { PlanFilterBar } from '@euler/app/flows/plans/pages/list/components/PlanFilterBar';
import {
  PlanFilterPanel,
  PlanPanelFilters,
} from '@euler/app/flows/plans/pages/list/components/PlanFilterPanel';
import {
  PlanDetailGroup,
  groupPlanDetails,
} from '@euler/app/flows/plans/pages/list/functions/groupPlanDetails';
import { useMaintenancePlanDetails } from '@euler/app/flows/plans/pages/list/functions/useMaintenancePlanDetails';
import { Center } from '@euler/components/Center';
import { EmptyView } from '@euler/components/EmptyView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { MaintenancePlanSearchParams } from '@euler/model';
import { usePersistFn } from '@euler/utils/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  Platform,
  RefreshControl,
} from 'react-native';

const keyExtractor = (group: PlanDetailGroup) => group.orderNo;

export const PlansScreen = wrapNavigatorScreen(
  () => {
    useAIAssistantForPage('maintenance_plans');
    const { rpx } = useResponsiveContext();

    const [filters, setFilters] = useState<MaintenancePlanSearchParams>({});
    const [searchText, setSearchText] = useState('');
    const [filtersShown, setFiltersShown] = useState(false);

    const {
      data,
      status,
      replace,
      refresh,
      fetchMore,
      fetchSinceLastError,
      update,
    } = useMaintenancePlanDetails();

    const groups = useMemo(() => groupPlanDetails(data), [data]);

    // Re-run the search whenever the committed filters change (incl. mount).
    useEffect(() => {
      void replace(filters);
    }, [filters, replace]);

    const onSearchSubmit = usePersistFn((text: string) => {
      setFilters(f => ({ ...f, keyword: text.trim() || undefined }));
    });

    const onApplyPanel = usePersistFn((next: PlanPanelFilters) => {
      // Only merge the filters; the sheet drives its own open/close (apply
      // dismisses → onClose; reset keeps it open).
      setFilters(f => ({
        ...f,
        subject: next.subject,
        nextServiceDate: next.nextServiceDate,
        leadConverted: next.leadConverted,
      }));
    });

    const activeFilterCount = useMemo(() => {
      let n = 0;
      if (filters.subject) n++;
      if (filters.nextServiceDate) n++;
      if (filters.leadConverted != null) n++;
      return n;
    }, [filters.subject, filters.nextServiceDate, filters.leadConverted]);

    const onItemEdited = usePersistFn(
      (detailId: number, patch: PlanDetailEditPatch) => {
        update(d => {
          if (d.id === detailId) {
            if (patch.name != null) d.name = patch.name;
            if (patch.nextServiceDate != null) {
              d.nextServiceDate = patch.nextServiceDate;
            }
            if (patch.nextServiceMileage != null) {
              d.nextServiceMileage = patch.nextServiceMileage;
            }
            if (patch.remark != null) d.remark = patch.remark;
          }
          return undefined;
        });
      },
    );

    const onItemRemoved = usePersistFn((detailId: number) => {
      update(d => (d.id === detailId ? 'remove' : undefined));
    });

    const onItemAlertChanged = usePersistFn(
      (
        detailId: number,
        settings: {
          isAlertEnabled: boolean;
          isAlertEnabledForCustomer: boolean;
        },
      ) => {
        update(d => {
          if (d.id === detailId) {
            d.isAlertEnabled = settings.isAlertEnabled;
            d.isAlertEnabledForCustomer = settings.isAlertEnabledForCustomer;
          }
          return undefined;
        });
      },
    );

    const onRefresh = useCallback(() => {
      void refresh(filters);
    }, [refresh, filters]);
    const onEndReached = useCallback(() => {
      // Don't paginate against a filter change still in flight — a late
      // fetchMore would append stale-filter rows under the new filter.
      if (status.isRefreshing) return;
      void fetchMore(filters);
    }, [fetchMore, filters, status.isRefreshing]);
    const onRetry = useCallback(() => {
      void fetchSinceLastError(filters);
    }, [fetchSinceLastError, filters]);

    const renderItem: ListRenderItem<PlanDetailGroup> = useCallback(
      ({ item }) => (
        <PlanDetailGroupCard
          group={item}
          onItemEdited={onItemEdited}
          onItemRemoved={onItemRemoved}
          onItemAlertChanged={onItemAlertChanged}
        />
      ),
      [onItemEdited, onItemRemoved, onItemAlertChanged],
    );

    return (
      <>
        <PlanFilterBar
          searchText={searchText}
          activeFilterCount={activeFilterCount}
          onSearchChange={setSearchText}
          onSearchSubmit={onSearchSubmit}
          onOpenFilters={() => setFiltersShown(true)}
        />
        <FlatList
          data={groups}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          showsVerticalScrollIndicator={Platform.OS === 'web'}
          contentContainerStyle={{ flexGrow: 1 }}
          refreshControl={
            Platform.OS === 'web' ? undefined : (
              <RefreshControl
                refreshing={(status.isRefreshing && data.length > 0) ?? false}
                onRefresh={onRefresh}
              />
            )
          }
          onEndReachedThreshold={0.3}
          onEndReached={onEndReached}
          ListEmptyComponent={
            status.isLoading && data.length === 0 && !status.isRefreshing ? (
              <Center
                css={`
                  flex: 1;
                `}
              >
                <DefaultLoader />
              </Center>
            ) : (
              <EmptyView
                error={status.error}
                onRetry={onRefresh}
                message={'暂无相关数据'}
              />
            )
          }
          ListHeaderComponent={
            Platform.OS === 'web' && status.isRefreshing && data.length > 0 ? (
              <Center
                css={`
                  padding: ${rpx(15)}px;
                `}
              >
                <ActivityIndicator />
              </Center>
            ) : null
          }
          ListFooterComponent={
            <FooterView
              isLoading={
                status.isLoading &&
                status.loadedDataLength > 0 &&
                !status.isRefreshing
              }
              total={data.length}
              error={status.error}
              hasMore={status.hasMore}
              noMoreText="没有更多数据了"
              onRetry={onRetry}
            />
          }
        />
        <PlanFilterPanel
          visible={filtersShown}
          value={filters}
          onClose={() => setFiltersShown(false)}
          onApply={onApplyPanel}
        />
      </>
    );
  },
  {
    title: '维保计划',
  },
);
